import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { lastValueFrom } from 'rxjs';

// Authentication provider interfaces
export interface IAuthProvider {
    id: string;
    enabled: boolean;
    displayName: string;
    description?: string;
    logoPath?: string; // Path to the provider's logo
    order?: number; // Optional ordering value
}

export interface IEntraIdProvider extends IAuthProvider {
    authority: string;
    clientId: string;
    loginRedirectUri: string;
    logoutRedirectUri: string;
}

export interface IB2CProvider extends IAuthProvider {
    tenant: string;
    clientId: string;
    policy: string;
}

export interface IClientSsoProvider extends IAuthProvider {
    clientId: string;
    provider: string; // The specific SSO provider code
}

// Authentication configuration
export interface IAuthConfig {
    defaultProvider?: string;
    entraId?: IEntraIdProvider;
    azureB2C?: IB2CProvider;
    clientSso?: {
        providers: IClientSsoProvider[];
        defaultProvider?: string;
    };
}

export interface IAzureB2CSettings {
    TenantName: string;
    TenantId: string;
    ClientId: string;
    ClientSecret?: string;
    SignInPolicyName: string;
    Issuers: string[];
}

export interface IAzureAdSettings {
    Instance: string;
    TenantId: string;
    ClientId: string;
    CallbackPath: string;
    RedirectPath: string;
    ClientSecret?: string;
    Issuers: string[];
}

export interface IAppConfig {
    client: string;
    apiUri: string;
    webUri: string;
    env: string;
    favIconPath: string;
    logoPath: string;
    instrumentationKey: string;
    shiftSSOClientId: string;
    shiftSSOAuthority: string;
    shiftSSOLoginRedirectUri: string;
    shiftSSOLogoutRedirectUri: string;
    clientSSOProvider: string;
    clientSSOClientId: string;
    clientSSOUrl: string;
    clientSSOIssuer: string;
    clientSSOAssertUrl: string;
    credentialSSOTenantName: string;
    credentialSSOTenantId: string;
    credentialSSOClientId: string;
    credentialSSOSignInPolicyName: string;
    credentialSSOAuthority: string;
    credentialSSOLoginRedirectUri: string;
    credentialSSOLogoutRedirectUri: string;
    flags: FeatureFlagResponse[];
    // Direct mappings from appsettings.json
    AzureB2C?: IAzureB2CSettings;
    AzureAd?: IAzureAdSettings;
    // New authentication configuration
    auth?: IAuthConfig;
    clientErrorScreenMessage: string;
}

export interface FeatureFlagResponse {
    name: string;
    enabled: boolean;
}

export enum FeatureFlagStatus {
    Enabled = 'Enabled',
    Disabled = 'Disabled',
    NotSet = 'NotSet'
}

@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
    constructor(private http: HttpClient) { }
    private readonly LocalDebugUri: string = 'https://localhost:5001'; //TODO: Move to environment file
    private _config: IAppConfig;
    async loadConfig(): Promise<IAppConfig> {
      const configUrl =  environment.debug ? this.LocalDebugUri + '/config' : '/config';
      console.debug('configUrl', configUrl);

      this._config = await lastValueFrom(this.http.get<IAppConfig>(configUrl))
        .catch(err => {
          console.error('Config file could not be loaded. Using default configuration.', err);
          return Promise.reject(err);
        });

      console.debug('config', this._config);

      // If auth configuration is missing, initialize with default values
      if (!this._config.auth) {
        this._config.auth = this.getDefaultAuthConfig();
      }

      return this._config;
    }

    get config() {
        return this._config;
    }

    /**
     * Returns the authentication configuration
     */
    get auth(): IAuthConfig {
        return this._config?.auth || this.getDefaultAuthConfig();
    }

    /**
     * Gets an array of all enabled authentication providers
     */
    get enabledAuthProviders(): IAuthProvider[] {
        const providers: IAuthProvider[] = [];

        if (this.auth.entraId?.enabled) {
            providers.push(this.auth.entraId);
        }

        if (this.auth.azureB2C?.enabled) {
            providers.push(this.auth.azureB2C);
        }

        if (this.auth.clientSso?.providers) {
            providers.push(...this.auth.clientSso.providers.filter(p => p.enabled));
        }

        // Sort by order if specified
        return providers.sort((a, b) =>
            (a.order !== undefined && b.order !== undefined)
                ? a.order - b.order
                : 0
        );
    }

    /**
     * Gets client SSO provider by ID
     */
    getClientSsoProvider(providerId: string): IClientSsoProvider | undefined {
        return this.auth.clientSso?.providers?.find(p => p.id === providerId);
    }

    /**
     * Creates default auth configuration from environment settings for backward compatibility
     */
    private getDefaultAuthConfig(): IAuthConfig {
        // Create default configuration based on existing environment settings
        return {
            entraId: {
                id: 'entraId',
                enabled: true,
                displayName: 'Sign in with Shift SSO',
                description: 'Use your Shift Digital account',
                logoPath: 'assets/images/shiftDigital.png',
                order: 1,
                authority: this._config?.shiftSSOAuthority,
                clientId: this._config?.shiftSSOClientId,
                loginRedirectUri: this._config?.shiftSSOLoginRedirectUri || environment.loginRedirectUrl,
                logoutRedirectUri: this._config?.shiftSSOLogoutRedirectUri || environment.logoutRedirectUrl
            },
            azureB2C: {
                id: 'azureB2C',
                enabled: true,
                displayName: 'Sign in directly',
                description: 'Use your username and password',
                order: 3,
                tenant: this._config?.credentialSSOTenantName || '',
                policy: 'B2C_1_SignIn',
                clientId: this._config?.credentialSSOClientId || '',
            },
            clientSso: {
                providers: [
                    {
                        id: 'clientSSO',
                        enabled: true,
                        displayName: 'Sign in with SSO',
                        description: 'Use your organizations single sign-on',
                        logoPath: `${this._config?.favIconPath || 'assets/images/favicon.ico'}`,
                        order: 2,
                        clientId: this._config?.clientSSOClientId || '',
                        provider: this._config?.clientSSOProvider || ''
                    }
                ],
                defaultProvider: 'clientSSO'
            }
        };
    }

    public getFeatureFlagStatus(featureFlagName: string): FeatureFlagStatus {
        const featureFlag = this._config?.flags?.find(flag => flag.name === featureFlagName);
        if (featureFlag) {
            return featureFlag.enabled ? FeatureFlagStatus.Enabled : FeatureFlagStatus.Disabled;
        }
        return FeatureFlagStatus.NotSet;
    }

    public isFeatureFlagEnabled(featureFlagName: string): boolean {
        const featureFlag = this._config?.flags?.find(flag => flag.name === featureFlagName);
        return featureFlag ? featureFlag.enabled : false;
    }
}